import debounce from 'lodash/debounce';
import {
    useCallback,
    useEffect,
    useState,
} from 'react';
import { useQueryProductByKey } from './useQueryProductByKey';

const LOOKUP_DEBOUNCE_MS = 300;

interface UseDebouncedProductLookupReturn {
    productKey: string;
    resolvedName: string | undefined;
    notFound: boolean;
    isLookingUp: boolean;
    setProductKey: (productKey: string) => void;
    reset: () => void;
}

export const useDebouncedProductLookup = (
    accessToken: string | undefined | false,
): UseDebouncedProductLookupReturn => {
    const [productKey, setProductKeyState] = useState('');
    const [debouncedProductKey, setDebouncedProductKey] = useState('');

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetProductKey = useCallback(
        debounce((key: string) => setDebouncedProductKey(key), LOOKUP_DEBOUNCE_MS),
        [],
    );

    useEffect(() => {
        debouncedSetProductKey(productKey);

        return () => {
            debouncedSetProductKey.cancel();
        };
    }, [productKey, debouncedSetProductKey]);

    const { data: productMatch, isFetching } = useQueryProductByKey(
        accessToken,
        debouncedProductKey,
        { enabled: !!accessToken && debouncedProductKey.length > 0 },
    );

    const isDebouncePending = productKey.trim() !== debouncedProductKey.trim();
    const resolvedName = !isDebouncePending && productMatch ? productMatch.productName : undefined;
    const notFound = !!debouncedProductKey && !isDebouncePending && !isFetching && !productMatch;
    const isLookingUp = !!productKey.trim() && !resolvedName && !notFound;

    const setProductKey = (nextProductKey: string): void => {
        setProductKeyState(nextProductKey);
    };

    const reset = useCallback((): void => {
        setProductKeyState('');
        setDebouncedProductKey('');
        debouncedSetProductKey.cancel();
    }, [debouncedSetProductKey]);

    return {
        productKey,
        resolvedName,
        notFound,
        isLookingUp,
        setProductKey,
        reset,
    };
};
